import {
  useBatchingCallback,
  useCallback,
  useEffect,
  useRef,
} from '@web-stories-wp/react';

// Keep the latest value of each field, with at most one editor update per frame.
export default function useFrameUpdates(onUpdate, minInterval = 0) {
  const callback = useRef(onUpdate);
  callback.current = onUpdate;
  const pending = useRef(null);
  const frame = useRef(null);
  const lastFrame = useRef(-Infinity);

  const cancel = useCallback(() => {
    if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    frame.current = null;
    pending.current = null;
    lastFrame.current = -Infinity;
  }, []);

  const flush = useBatchingCallback(() => {
    if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    frame.current = null;
    const changes = pending.current;
    pending.current = null;
    if (changes) callback.current(changes);
  }, []);

  const onFrame = useCallback(
    function runFrame(time) {
      if (time - lastFrame.current < minInterval) {
        frame.current = window.requestAnimationFrame(runFrame);
        return;
      }
      lastFrame.current = time;
      flush();
    },
    [flush, minInterval]
  );

  const queue = useCallback(
    (changes) => {
      pending.current = { ...pending.current, ...changes };
      if (frame.current === null)
        frame.current = window.requestAnimationFrame(onFrame);
    },
    [onFrame]
  );

  // A changed selection unmounts the dialog; never replay its pending edits.
  useEffect(() => cancel, [cancel]);

  return { queue, flush, cancel };
}
